import React, { useState } from 'react';
import { Github, ExternalLink, Image } from 'lucide-react';
import { projects } from '../data/portfolioData';

const C = {
  cyan: '#00f5ff', green: '#00ff88', purple: '#7b2fff',
  gray: '#4a6275', white: '#e8f4f8',
  border: 'rgba(0,245,255,0.12)',
  card: 'rgba(0,245,255,0.025)',
};

const categories = ['All', 'Machine Learning', 'Web Development', 'AI & LLM', 'Data Science'];

const catColor = {
  'Machine Learning': { bg: 'rgba(0,255,136,0.1)', border: 'rgba(0,255,136,0.3)', color: '#00ff88' },
  'Web Development':  { bg: 'rgba(0,245,255,0.1)', border: 'rgba(0,245,255,0.3)', color: '#00f5ff' },
  'AI & LLM':         { bg: 'rgba(123,47,255,0.12)', border: 'rgba(123,47,255,0.35)', color: '#b48dff' },
  'Data Science':     { bg: 'rgba(251,191,36,0.1)', border: 'rgba(251,191,36,0.3)', color: '#fbbf24' },
};

export default function Projects() {
  const [activeFilter, setActiveFilter] = useState('All');
  const [hovered, setHovered] = useState(null);

  const filtered = activeFilter === 'All'
    ? projects
    : projects.filter(p => p.category === activeFilter);

  return (
    <section id="projects">
      {/* Header */}
      <div style={{ marginBottom: 48 }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          fontFamily: "'Space Mono', monospace", fontSize: 10,
          letterSpacing: 4, textTransform: 'uppercase', color: C.cyan, marginBottom: 12,
        }}>
          <span style={{ width: 32, height: 1, background: C.cyan, display: 'inline-block' }} />
          Portfolio
        </div>
        <h2 style={{
          fontFamily: "'Orbitron', monospace",
          fontSize: 'clamp(26px, 4vw, 44px)',
          fontWeight: 700, color: C.white, letterSpacing: -1,
        }}>
          My <span style={{ color: C.cyan }}>Projects</span>
        </h2>
        <p style={{ fontSize: 15, color: C.gray, marginTop: 12, maxWidth: 500 }}>
          A collection of machine learning, web development, and AI projects.
          Replace the placeholder slots with your actual project details.
        </p>
      </div>

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 36, flexWrap: 'wrap' }}>
        {categories.map(cat => (
          <button key={cat}
            onClick={() => setActiveFilter(cat)}
            style={{
              fontFamily: "'Space Mono', monospace", fontSize: 9,
              letterSpacing: 2, textTransform: 'uppercase',
              padding: '8px 18px',
              border: `1px solid ${activeFilter === cat ? C.cyan : C.border}`,
              color: activeFilter === cat ? C.cyan : C.gray,
              background: activeFilter === cat ? 'rgba(0,245,255,0.06)' : 'transparent',
              cursor: 'pointer', transition: 'all .2s',
            }}
            onMouseEnter={e => { if (activeFilter !== cat) e.currentTarget.style.borderColor = 'rgba(0,245,255,0.35)'; }}
            onMouseLeave={e => { if (activeFilter !== cat) e.currentTarget.style.borderColor = C.border; }}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Projects grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
        gap: 22,
      }}>
        {filtered.map((proj, i) => {
          const isHov = hovered === proj.id;
          const cc = catColor[proj.category] || {};
          return (
            <div key={proj.id}
              onMouseEnter={() => setHovered(proj.id)}
              onMouseLeave={() => setHovered(null)}
              style={{
                background: C.card,
                border: `1px solid ${isHov ? 'rgba(0,245,255,0.4)' : C.border}`,
                overflow: 'hidden',
                transition: 'all .35s ease',
                transform: isHov ? 'translateY(-7px)' : 'translateY(0)',
                boxShadow: isHov ? '0 24px 60px rgba(0,0,0,0.4), 0 0 30px rgba(0,245,255,0.08)' : 'none',
                position: 'relative',
              }}
            >
              {proj.featured && (
                <div style={{
                  position: 'absolute', top: 12, right: 12, zIndex: 2,
                  fontFamily: "'Space Mono', monospace", fontSize: 8,
                  letterSpacing: 2, textTransform: 'uppercase',
                  padding: '3px 8px',
                  background: 'rgba(0,245,255,0.15)',
                  border: '1px solid rgba(0,245,255,0.4)',
                  color: C.cyan,
                }}>
                  FEATURED
                </div>
              )}

              {/* Image area */}
              <div style={{
                width: '100%', height: 195,
                background: '#060c14',
                position: 'relative', overflow: 'hidden',
              }}>
                {proj.image ? (
                  <img
                    src={proj.image} alt={proj.name}
                    style={{
                      width: '100%', height: '100%', objectFit: 'cover',
                      transition: 'transform .5s ease',
                      transform: isHov ? 'scale(1.06)' : 'scale(1)',
                    }}
                  />
                ) : (
                  // Placeholder
                  <div style={{
                    width: '100%', height: '100%',
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center', gap: 10,
                    background: `repeating-linear-gradient(
                      45deg,
                      rgba(0,245,255,0.018), rgba(0,245,255,0.018) 10px,
                      transparent 10px, transparent 20px
                    )`,
                  }}>
                    <div style={{ fontSize: 40, opacity: 0.5 }}>{proj.imagePlaceholder}</div>
                    <div style={{
                      fontFamily: "'Space Mono', monospace", fontSize: 8,
                      letterSpacing: 3, color: C.gray, textTransform: 'uppercase',
                      display: 'flex', alignItems: 'center', gap: 6,
                    }}>
                      <Image size={10} /> Add your image
                    </div>
                    {/* Corner marks */}
                    {[
                      { top: 8, left: 8 }, { top: 8, right: 8 },
                      { bottom: 8, left: 8 }, { bottom: 8, right: 8 },
                    ].map((pos, pi) => (
                      <div key={pi} style={{
                        position: 'absolute', width: 14, height: 14,
                        borderTop: pi < 2 ? `1px solid rgba(0,245,255,0.2)` : 'none',
                        borderBottom: pi >= 2 ? `1px solid rgba(0,245,255,0.2)` : 'none',
                        borderLeft: pi % 2 === 0 ? `1px solid rgba(0,245,255,0.2)` : 'none',
                        borderRight: pi % 2 === 1 ? `1px solid rgba(0,245,255,0.2)` : 'none',
                        ...pos,
                      }} />
                    ))}
                  </div>
                )}

                {/* Category badge */}
                <div style={{
                  position: 'absolute', top: 12, left: 12,
                  fontFamily: "'Space Mono', monospace", fontSize: 8,
                  letterSpacing: 2, textTransform: 'uppercase',
                  padding: '3px 10px',
                  background: cc.bg, border: `1px solid ${cc.border}`,
                  color: cc.color,
                }}>
                  {proj.category}
                </div>
              </div>

              {/* Body */}
              <div style={{ padding: '22px 22px 18px' }}>
                <h3 style={{
                  fontFamily: "'Orbitron', monospace",
                  fontSize: 15, fontWeight: 600, color: C.white,
                  letterSpacing: 0.5, marginBottom: 10,
                }}>
                  {proj.name}
                </h3>
                <p style={{
                  fontSize: 13.5, lineHeight: 1.75,
                  color: 'rgba(232,244,248,0.52)', marginBottom: 18,
                }}>
                  {proj.desc}
                </p>

                {/* Tools */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 20 }}>
                  {proj.tools.map((tool, j) => (
                    <span key={j} style={{
                      fontFamily: "'Space Mono', monospace", fontSize: 8,
                      letterSpacing: 1.5, padding: '3px 9px',
                      background: 'rgba(123,47,255,0.1)',
                      border: '1px solid rgba(123,47,255,0.3)',
                      color: '#b48dff',
                      textTransform: 'uppercase',
                    }}>
                      {tool}
                    </span>
                  ))}
                </div>

                {/* Links */}
                <div style={{ display: 'flex', gap: 10 }}>
                  <a href={proj.github} target="_blank" rel="noreferrer"
                    style={{
                      fontFamily: "'Space Mono', monospace", fontSize: 9,
                      letterSpacing: 2, textTransform: 'uppercase',
                      padding: '7px 14px',
                      border: `1px solid ${C.border}`,
                      color: C.gray, textDecoration: 'none',
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      transition: 'all .2s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = C.cyan; e.currentTarget.style.color = C.cyan; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.gray; }}
                  >
                    <Github size={11} /> Code
                  </a>
                  <a href={proj.demo} target="_blank" rel="noreferrer"
                    style={{
                      fontFamily: "'Space Mono', monospace", fontSize: 9,
                      letterSpacing: 2, textTransform: 'uppercase',
                      padding: '7px 14px',
                      border: `1px solid ${C.cyan}`,
                      color: C.cyan, textDecoration: 'none',
                      background: 'rgba(0,245,255,0.05)',
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      transition: 'all .2s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,245,255,0.12)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(0,245,255,0.05)'; }}
                  >
                    <ExternalLink size={11} /> Live Demo
                  </a>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
